import { createAsyncThunk } from '@reduxjs/toolkit';
import fetchWeatherData from 'utils/fetchWeatherData';

/** How long the current weather is reused before the dashboard asks again. */
export const WEATHER_MAX_AGE_MS = 15 * 60 * 1000;

/** Whether the project says where it is: coordinates, or at least a city. */
export const hasWeatherLocation = (project) =>
  Boolean((project?.latitude && project?.longitude) || project?.city);

// The site itself when the project has coordinates (the weather API prefers a
// city it is given), its city otherwise.
const weatherLocationOf = (project) =>
  project.latitude && project.longitude
    ? {
        id: project.id,
        latitude: project.latitude,
        longitude: project.longitude,
      }
    : { id: project.id, city: project.city };

const numberOrNull = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Today's forecast at the site, from the API's first forecast day
 * (`forecast.forecastday[0]`): its date, the day's lowest and highest
 * temperature (°C) and its hours (`time` 'YYYY-MM-DD HH:mm' in the site's
 * time, the temperature in °C and the precipitation in mm), in the API's
 * order (00:00 first). Null when the API gave no forecast day; hours without
 * a time are left out.
 * @param {Object} data the weather API's answer
 * @returns {{date: string|null, minTemperature: number|null, maxTemperature: number|null, hours: Array<{time: string, temperature: number|null, precipitation: number|null}>}|null}
 */
export const weatherTodayOf = (data) => {
  const forecastDay = data?.forecast?.forecastday?.[0];
  if (!forecastDay) return null;
  const hours = Array.isArray(forecastDay.hour) ? forecastDay.hour : [];
  return {
    date: forecastDay.date || null,
    minTemperature: numberOrNull(forecastDay.day?.mintemp_c),
    maxTemperature: numberOrNull(forecastDay.day?.maxtemp_c),
    hours: hours
      .filter((hour) => typeof hour?.time === 'string' && hour.time)
      .map((hour) => ({
        time: hour.time,
        temperature: numberOrNull(hour.temp_c),
        precipitation: numberOrNull(hour.precip_mm),
      })),
  };
};

/**
 * The current weather at the project's site, from the weather API the forms
 * already use (weatherapi.com's forecast for today, its current conditions):
 * the temperature (°C), the air pressure (hPa) and the humidity (%), the
 * condition's code and whether it is day (for a glyph), where the reading is
 * from and when it was taken. `current` is null when the project has no
 * location or the API gives no current conditions. `today` is the day's
 * forecast (weatherTodayOf): its hours and its lowest and highest
 * temperature, which the API answers with the current conditions (every hour
 * of the day: no `hour` asked for). Reused for WEATHER_MAX_AGE_MS.
 */
export const loadProjectWeather = createAsyncThunk(
  'projectOverview/loadWeather',
  async ({ project }) => {
    if (!hasWeatherLocation(project)) {
      return { loadedAt: Date.now(), current: null, today: null };
    }
    const data = await fetchWeatherData({
      project: weatherLocationOf(project),
      // No hours: the API answers with every hour of the day.
      hours: [],
    });
    const current = data?.current;
    return {
      loadedAt: Date.now(),
      current: current
        ? {
            temperature: numberOrNull(current.temp_c),
            pressure: numberOrNull(current.pressure_mb),
            humidity: numberOrNull(current.humidity),
            conditionCode: numberOrNull(current.condition?.code),
            isDay:
              current.is_day === undefined || current.is_day === null
                ? null
                : Boolean(current.is_day),
            place: data?.location?.name || null,
            updatedAt: current.last_updated || null,
          }
        : null,
      today: weatherTodayOf(data),
    };
  },
  {
    // Once per project at a time, and not again while fresh.
    condition: ({ project, force }, { getState }) => {
      if (!project?.id) return false;
      const entry = getState().projectOverview?.weather?.[project.id];
      if (!entry) return true;
      if (entry.status === 'loading') return false;
      if (force) return true;
      // An entry kept from before the day's forecast was (no `today` at all):
      // asked again for it.
      if (entry.status === 'succeeded' && entry.today === undefined) {
        return true;
      }
      return !(
        entry.status === 'succeeded' &&
        Date.now() - entry.loadedAt < WEATHER_MAX_AGE_MS
      );
    },
  }
);
